import type { ResolvedStatisticsDateRange } from '../types/date-range';
import { ApiFetcher } from '@/utils/api-service';

export type ViewByType =
  | 'age'
  | 'gender'
  | 'province'
  | 'health_status'
  | 'age_gender'
  | 'age_province'
  | 'age_health'
  | 'health_gender'
  | 'province_gender'
  | 'province_health'
  | 'custom';

export interface ProfileStatsParams {
  target?: 'user' | 'relative' | 'all';
  view_by?: ViewByType;
  period?: 'today' | 'this_month' | 'this_year' | 'all' | 'custom';
  sub_filter?: string;
  sub_filter_2?: string;
  profile_types?: string;
  search_name?: string;
  search_phone?: string;
  search_email?: string;
  search_user_id?: string;
  genders?: string;
  provinces?: string;
  age_groups?: string;
  dob_from?: string;
  dob_to?: string;
  created_from?: string;
  created_to?: string;
  from_date?: string;
  to_date?: string;
  offset?: number;
  limit?: number;
}

export interface ChartDataItem {
  code?: string;
  name: string;
  value: number;
  percentage?: number;
}

export interface StackedSeriesItem {
  name: string;
  data: number[];
}

export interface StackedChartData {
  categories: string[];
  series: StackedSeriesItem[];
}

export interface ProfileDetailItem {
  userId?: number | string | null;
  creatorUserId?: number | string | null;
  fullName: string | null;
  phone: string | null;
  email: string | null;
  dob?: string | null;
  gender?: string | null;
  age: number | string | null;
  province: string | null;
  createdDate: string | null;
}

export interface StatisticsProfileResponse {
  summary: {
    resolved_date_range?: { from_date: string | null; to_date: string | null };
    totalProfiles: number;
    chartData?: ChartDataItem[];
    stackedChartData?: StackedChartData;
  };
  detail: {
    results: ProfileDetailItem[];
    paging: {
      offset: number;
      limit: number;
      totalRows: number;
      totalPage: number;
    };
  };
}

export const getStatisticsProfiles = async (
  params: ProfileStatsParams
): Promise<StatisticsProfileResponse> => {
  const host = import.meta.env.VITE_API_HOST || 'http://localhost:7071';
  const fetcher = (window as any).apiFetcher || new ApiFetcher(host);

  const queryParams: Record<string, any> = {
    target: params.target || 'user',
    view_by: params.view_by || 'age',
    period: params.period || 'all',
    sub_filter: params.sub_filter || undefined,
    sub_filter_2: params.sub_filter_2 || undefined,
    profile_types: params.profile_types || undefined,
    search_name: params.search_name || undefined,
    search_phone: params.search_phone || undefined,
    search_email: params.search_email || undefined,
    search_user_id: params.search_user_id || undefined,
    genders: params.genders || undefined,
    provinces: params.provinces || undefined,
    age_groups: params.age_groups || undefined,
    dob_from: params.dob_from || undefined,
    dob_to: params.dob_to || undefined,
    created_from: params.created_from || undefined,
    created_to: params.created_to || undefined,
    offset: params.offset || 0,
    limit: params.limit || 10
  };

  if (params.period === 'custom') {
    if (params.from_date) queryParams.from_date = params.from_date;
    if (params.to_date) {
      queryParams.to_date = params.to_date;
    }
  }

  const response = await fetcher.get('statistics/profiles/get', {
    query: queryParams
  });

  return response.data || response;
};

export interface ProfileExportFilterItem {
  label: string;
  value: string;
}

export interface ProfileExportMeta {
  target_label?: string;
  view_by_label?: string;
  chart_title?: string;
  filters?: ProfileExportFilterItem[];
  time_range_label?: string;
}

export const exportStatisticsProfilesExcel = async (
  params: ProfileStatsParams,
  chartImageBase64?: string,
  meta?: ProfileExportMeta
): Promise<void> => {
  const fetcher = getApiFetcher();

  const queryParams: Partial<ProfileStatsParams> = {
    target: params.target || 'user',
    view_by: params.view_by || 'age',
    period: params.period || 'all',
    sub_filter: params.sub_filter || undefined,
    sub_filter_2: params.sub_filter_2 || undefined,
    from_date: params.from_date || undefined,
    to_date: params.to_date || undefined,
    profile_types: params.profile_types || undefined,
    search_name: params.search_name || undefined,
    search_phone: params.search_phone || undefined,
    search_email: params.search_email || undefined,
    search_user_id: params.search_user_id || undefined,
    genders: params.genders || undefined,
    provinces: params.provinces || undefined,
    age_groups: params.age_groups || undefined,
    dob_from: params.dob_from || undefined,
    dob_to: params.dob_to || undefined,
    created_from: params.created_from || undefined,
    created_to: params.created_to || undefined,
  };

  let blob: Blob;
  try {
    const response = await fetcher.post(
      'statistics/profiles/export',
      { queryParams, chart_image_base64: chartImageBase64, meta },
      { responseType: 'blob' }
    );
    blob = response instanceof Blob ? response : new Blob([response]);
  } catch (error: any) {
    const response = error.response || error;
    if (response.status === 422) {
      const errorData = response.data instanceof Blob
        ? JSON.parse(await response.data.text())
        : response.data;
      throw new Error(errorData?.message || 'Số lượng bản ghi vượt quá giới hạn cho phép.');
    }
    throw new Error('Không thể xuất báo cáo Excel. Vui lòng thử lại sau.');
  }

  const today = new Date();
  const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
  const filename = `Bao_cao_thong_ke_ho_so_${dateStr}.xlsx`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export interface AccessOverviewParams {
  period?: 'today' | 'this_month' | 'this_year' | 'last_30_days' | 'last_12_months' | 'last_3_years' | 'custom' | 'all';
  from_date?: string;
  to_date?: string;
}

export interface AccessOverviewResponse {
  resolved_date_range?: ResolvedStatisticsDateRange;
  cards: {
    averageAccess: number;
    averageUnit: string;
    averages?: {
      daily?: number;
      monthly?: number;
      yearly?: number;
    };
    totalAccess: number;
    activeUsers: number;
    inactiveUsers: number;
  };
  featureChartData: ChartDataItem[];
  trendChartData: {
    isDaily: boolean;
    categories: string[];
    series: StackedSeriesItem[];
  };
}

export const getAccessOverviewData = async (
  params: AccessOverviewParams
): Promise<AccessOverviewResponse> => {
  const host = import.meta.env.VITE_API_HOST || 'http://localhost:7071';
  const fetcher = (window as any).apiFetcher || new ApiFetcher(host);

  const queryParams: Record<string, any> = {
    period: params.period || (params.from_date || params.to_date ? 'custom' : 'all'),
    from_date: params.from_date || undefined,
    to_date: params.to_date || undefined,
  };

  const response = await fetcher.get('statistics/access/overview', {
    query: queryParams,
  });

  return response.data || response;
};

// ─── R2.2 / R2.3 / R2.4 Types ────────────────────────────────────────────────

export interface AccessSessionItem {
  logId?: number;
  sessionId: string;
  accessTime: string;
  userId: number | null;
  fullName: string | null;
  phone: string | null;
  email: string | null;
  gender: string | null;
  province: string | null;
  age: number | null;
  feature: string;
  deviceType: string;
  duration?: number;
}

export interface AccessListResponse {
  resolved_date_range?: ResolvedStatisticsDateRange;
  items: AccessSessionItem[];
  total: number;
}

export interface AccessUserChartParams {
  period?: string;
  from_date?: string;
  to_date?: string;
  view_by?: string;
  age_groups?: string[];
  genders?: string[];
  provinces?: string[];
  features?: string[];
  devices?: string[];
}

export interface AccessFrequencyChartParams {
  period?: string;
  from_date?: string;
  to_date?: string;
  view_by?: string;
  features?: string[];
  frequency_ranges?: string[];
}

export interface AccessAvgTimeParams {
  period?: string;
  from_date?: string;
  to_date?: string;
}

// ─── Helper ───────────────────────────────────────────────────────────────────

function getApiFetcher() {
  const host = import.meta.env.VITE_API_HOST || 'http://localhost:7071';
  return (window as any).apiFetcher || new ApiFetcher(host);
}

// Encode at the Statistics boundary because the shared fetcher joins raw query values.
function getAccessReport(path: string, query: Record<string, unknown>) {
  const encoded = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) encoded.append(key, String(item));
    } else {
      encoded.set(key, String(value));
    }
  }
  return getApiFetcher().get(`${path}?${encoded.toString()}`);
}

// ─── R2.2 — Sub-tab Người dùng ────────────────────────────────────────────────

export const getAccessUserChartData = async (params: AccessUserChartParams): Promise<any> => {
  const query: Record<string, any> = {
    period: params.period || 'all',
    view_by: params.view_by || 'age',
  };
  if (params.from_date) query['from_date'] = params.from_date;
  if (params.to_date) query['to_date'] = params.to_date;
  if (params.age_groups?.length) query['age_groups[]'] = params.age_groups;
  if (params.genders?.length) query['genders[]'] = params.genders;
  if (params.provinces?.length) query['provinces[]'] = params.provinces;
  if (params.features?.length) query['features[]'] = params.features;
  if (params.devices?.length) query['devices[]'] = params.devices;
  const response = await getAccessReport('statistics/access/user/chart', query);
  return response.data || response;
};

export const getAccessUserListData = async (
  params: AccessUserChartParams & { page?: number; limit?: number }
): Promise<AccessListResponse> => {
  const query: Record<string, any> = {
    period: params.period || 'all',
    view_by: params.view_by || 'age',
    page: params.page || 1,
    limit: params.limit || 10,
  };
  if (params.from_date) query['from_date'] = params.from_date;
  if (params.to_date) query['to_date'] = params.to_date;
  if (params.age_groups?.length) query['age_groups[]'] = params.age_groups;
  if (params.genders?.length) query['genders[]'] = params.genders;
  if (params.provinces?.length) query['provinces[]'] = params.provinces;
  if (params.features?.length) query['features[]'] = params.features;
  if (params.devices?.length) query['devices[]'] = params.devices;
  const response = await getAccessReport('statistics/access/user/list', query);
  return response.data || response;
};

export interface AccessUserCustomParams {
  period?: string; from_date?: string; to_date?: string;
  name?: string; phone?: string; email?: string;
  genders?: string[]; age_groups?: string[]; provinces?: string[];
  features?: string[]; devices?: string[]; user_ids?: string;
  access_from?: string; access_to?: string;
  page?: number; limit?: number;
}

export const getAccessUserCustomListData = async (params: AccessUserCustomParams): Promise<AccessListResponse> => {
  const query: Record<string, any> = { page: params.page || 1, limit: params.limit || 10 };
  if (params.period) query['period'] = params.period;
  if (params.from_date) query['from_date'] = params.from_date;
  if (params.to_date) query['to_date'] = params.to_date;
  if (params.name) query['name'] = params.name;
  if (params.phone) query['phone'] = params.phone;
  if (params.email) query['email'] = params.email;
  if (params.user_ids) query['user_ids'] = params.user_ids;
  if (params.access_from) query['access_from'] = params.access_from;
  if (params.access_to) query['access_to'] = params.access_to;
  if (params.genders?.length) query['genders[]'] = params.genders;
  if (params.age_groups?.length) query['age_groups[]'] = params.age_groups;
  if (params.provinces?.length) query['provinces[]'] = params.provinces;
  if (params.features?.length) query['features[]'] = params.features;
  if (params.devices?.length) query['devices[]'] = params.devices;
  const response = await getAccessReport('statistics/access/user/custom-list', query);
  return response.data || response;
};

export interface AccessExportFilterItem {
  label: string;
  value: string;
}

export interface AccessExportMeta {
  tab_title?: string;
  view_by_label?: string;
  chart_title?: string;
  filters?: AccessExportFilterItem[];
  time_range_label?: string;
}

export const exportAccessUserExcel = async (
  queryParams: AccessUserChartParams & AccessUserCustomParams, chartImageBase64?: string, isCustomMode?: boolean, meta?: AccessExportMeta
): Promise<Blob> => {
  const fetcher = getApiFetcher();
  const response = await fetcher.post(
    'statistics/access/user/export',
    { queryParams, chart_image_base64: chartImageBase64, is_custom_mode: isCustomMode, meta },
    { responseType: 'blob' }
  );
  return response instanceof Blob ? response : new Blob([response]);
};

// ─── R2.3 — Sub-tab Tần suất ──────────────────────────────────────────────────

export const getAccessFrequencyChartData = async (params: AccessFrequencyChartParams): Promise<any> => {
  const query: Record<string, any> = {
    period: params.period || 'all',
    view_by: params.view_by || 'frequency_system',
  };
  if (params.from_date) query['from_date'] = params.from_date;
  if (params.to_date) query['to_date'] = params.to_date;
  if (params.features?.length) query['features[]'] = params.features;
  if (params.frequency_ranges?.length) query['frequency_ranges[]'] = params.frequency_ranges;
  const response = await getAccessReport('statistics/access/frequency/chart', query);
  return response.data || response;
};

export const getAccessFrequencyListData = async (
  params: AccessFrequencyChartParams & { page?: number; limit?: number }
): Promise<AccessListResponse> => {
  const query: Record<string, any> = { view_by: params.view_by || 'frequency_system', period: params.period || 'all', page: params.page || 1, limit: params.limit || 10 };
  if (params.from_date) query['from_date'] = params.from_date;
  if (params.to_date) query['to_date'] = params.to_date;
  if (params.features?.length) query['features[]'] = params.features;
  if (params.frequency_ranges?.length) query['frequency_ranges[]'] = params.frequency_ranges;
  const response = await getAccessReport('statistics/access/frequency/list', query);
  return response.data || response;
};

export const exportAccessFrequencyExcel = async (queryParams: AccessFrequencyChartParams & { page?: number; limit?: number }, chartImageBase64?: string, meta?: AccessExportMeta): Promise<Blob> => {
  const fetcher = getApiFetcher();
  const response = await fetcher.post(
    'statistics/access/frequency/export',
    { queryParams, chart_image_base64: chartImageBase64, meta },
    { responseType: 'blob' }
  );
  return response instanceof Blob ? response : new Blob([response]);
};

// ─── R2.4 — Sub-tab Thời gian trung bình ─────────────────────────────────────

export const getAccessAvgTimeChartData = async (params: AccessAvgTimeParams): Promise<any> => {
  const response = await getAccessReport('statistics/access/avg-time/chart', { period: params.period || 'all', from_date: params.from_date, to_date: params.to_date });
  return response.data || response;
};

export const getAccessAvgTimeListData = async (
  params: AccessAvgTimeParams & { page?: number; limit?: number }
): Promise<AccessListResponse> => {
  const response = await getAccessReport('statistics/access/avg-time/list', { period: params.period || 'all', from_date: params.from_date, to_date: params.to_date, page: params.page || 1, limit: params.limit || 10 });
  return response.data || response;
};

export const exportAccessAvgTimeExcel = async (queryParams: AccessAvgTimeParams & { page?: number; limit?: number }, chartImageBase64?: string, meta?: AccessExportMeta): Promise<Blob> => {
  const fetcher = getApiFetcher();
  const response = await fetcher.post(
    'statistics/access/avg-time/export',
    { queryParams, chart_image_base64: chartImageBase64, meta },
    { responseType: 'blob' }
  );
  return response instanceof Blob ? response : new Blob([response]);
};

// ─── Overview Export ─────────────────────────────────────────────────────────

export const exportAccessOverviewExcel = async (
  overviewImageBase64: string,
  timeRangeLabel?: string
): Promise<Blob> => {
  const fetcher = getApiFetcher();
  const response = await fetcher.post(
    'statistics/access/overview/export',
    { overview_image_base64: overviewImageBase64, time_range_label: timeRangeLabel },
    { responseType: 'blob' }
  );
  return response instanceof Blob ? response : new Blob([response]);
};
